import { Box, Text } from '@bonusly/design-system';

/**
 * Re-usable inline segments that match the visual treatment used by
 * the real recognition feed (`src/components/bonus/text-preview.tsx`).
 *
 * Why duplicate? `TextPreview` takes a single `text` string and runs
 * the bonus-text tokenizer on it — that's the right shape for already-
 * composed recognition reasons. The Bizy draft cards have TYPED fields
 * (giver, recipient, amount, message, hashtags) and never see the
 * composed text, so we render the same visual primitives directly
 * instead of round-tripping through a string + tokenizer.
 *
 * Keep the styling values aligned with `bonus/text-preview.tsx` and
 * the design-system semantic tokens (`semantic-tokens.ts`):
 *   - Amount:    `points.fg`   on `points.bgAlpha` pill, font-weight medium
 *   - Recipient: `primary.fg`,                            font-weight medium
 *   - Hashtag:   `hashtag.fg` (semantic token → purple.600 in default theme)
 *
 * If those tokens move in the design system, both this file and
 * text-preview.tsx pick up the change automatically — they reference
 * the same semantic names rather than literal palette colors.
 */

export function AmountPill({ amount }: { amount: number }) {
	return (
		<Box as="span" display="inline-flex" bgColor="points.bgAlpha" borderRadius="full" alignItems="center" px={2} py={0} verticalAlign="baseline">
			<Text as="span" fontSize="inherit" color="points.fg" fontWeight="medium">
				+{amount}
			</Text>
		</Box>
	);
}

export function RecipientHandle({ username }: { username: string }) {
	const handle = username.startsWith('@') ? username : `@${username}`;
	return (
		<Text as="span" fontSize="inherit" color="primary.fg" fontWeight="medium">
			{handle}
		</Text>
	);
}

export function HashtagToken({ hashtag }: { hashtag: string }) {
	const tag = hashtag.startsWith('#') ? hashtag : `#${hashtag}`;
	return (
		<Text as="span" fontSize="inherit" color="hashtag.fg" fontWeight="medium">
			{tag}
		</Text>
	);
}
